
export default function ProcessSection() {
  return (
    <section id="process" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our Simple <span className="text-teal-600">Website Design Process</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Getting a professional small business website in El Paso has never been easier. Our streamlined 
            process ensures you get exactly what you need without the complexity or lengthy timelines.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/84f7c961e93fd7343745fa38a0ec0dde.jpg"
              alt="Web design consultation meeting for small business website"
              className="w-full h-80 object-cover rounded-2xl shadow-xl"
            />
          </div>
          
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Start with a Free Consultation
            </h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Every successful small business website begins with understanding your unique goals, target audience, 
              and business objectives. During our free consultation, we'll discuss your vision, analyze your 
              competition, and identify the key features that will help your El Paso business succeed online.
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              We'll also review your existing branding, discuss your budget, and timeline expectations. This 
              consultation ensures we create a website that perfectly aligns with your business goals and 
              delivers measurable results from day one.
            </p>
            <div className="bg-teal-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold text-gray-900 mb-3">What We'll Cover:</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center space-x-2">
                  <i className="ri-check-line text-teal-600"></i>
                  <span>Your business goals and target customers</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-check-line text-teal-600"></i>
                  <span>Competitor analysis and market positioning</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-check-line text-teal-600"></i>
                  <span>Essential features and functionality needs</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-check-line text-teal-600"></i>
                  <span>Timeline, budget, and project scope</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl font-bold text-teal-600">1</span>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-4">Discovery & Planning</h4>
            <p className="text-gray-600 leading-relaxed">
              We analyze your business, research your competitors, and create a detailed strategy for your 
              website's design and functionality.
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl font-bold text-teal-600">2</span>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-4">Design & Development</h4>
            <p className="text-gray-600 leading-relaxed">
              Our team creates custom designs and develops your website using the latest technologies, 
              ensuring it's fast, secure, and mobile-responsive.
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl font-bold text-teal-600">3</span>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-4">Review & Refine</h4>
            <p className="text-gray-600 leading-relaxed">
              You'll review the completed website and request any changes. We'll refine everything until 
              you're completely satisfied with the results.
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl font-bold text-teal-600">4</span>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-4">Launch & Support</h4>
            <p className="text-gray-600 leading-relaxed">
              We launch your website and provide ongoing support to ensure it continues to perform at its 
              best and help grow your business.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl p-12 text-white">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-6">Fast Turnaround, Quality Results</h3>
              <p className="text-xl mb-6 leading-relaxed opacity-90">
                Unlike other web design companies that take months to deliver, we pride ourselves on 
                quick turnaround times without compromising on quality.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-time-line text-white"></i>
                  </div>
                  <div>
                    <div className="font-semibold">24-48 Hour Initial Design</div>
                    <div className="opacity-80">See your website concept within 2 days</div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-rocket-line text-white"></i>
                  </div>
                  <div>
                    <div className="font-semibold">1-2 Week Project Completion</div>
                    <div className="opacity-80">Most websites launched within 14 days</div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-customer-service-2-line text-white"></i>
                  </div>
                  <div>
                    <div className="font-semibold">Unlimited Revisions</div>
                    <div className="opacity-80">We'll perfect your site until you love it</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <img
                src="https://readdy.ai/api/search-image?query=fast%20delivery%20rocket%20launch%20concept%2C%20speed%20and%20efficiency%20visualization%2C%20modern%20minimalist%20design%2C%20professional%20service%20delivery%2C%20time%20management%20concept%2C%20clean%20background&width=500&height=400&seq=fast-delivery-6&orientation=landscape"
                alt="Fast website delivery and quick turnaround time"
                className="w-full h-64 object-cover rounded-xl shadow-lg opacity-90"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
